import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const AdminSetup = () => {
  const [email, setEmail] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);

  const handleMakeAdmin = async (e) => {
    e.preventDefault();

    if (!email || !secretKey) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/admin/make-admin', {
        email,
        secretKey
      });

      toast.success(response.data.message);
      toast.info('You can now login and access the admin dashboard at /admin');

      // Clear form
      setEmail('');
      setSecretKey('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error making user admin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Setup - VestrioZ</title>
        <meta
          name="description"
          content="Setup admin access for VestrioZ platform."
        />
        <meta name="keywords" content="admin setup, VestrioZ" />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1EB' }}>
        <div className="max-w-md w-full mx-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#222831' }}>
              <i className="fas fa-crown text-white text-2xl"></i>
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#222831' }}>
              VestrioZ Admin Setup
            </h1>
            <p className="text-gray-600">
              Make your account an administrator
            </p>
          </div>

          {/* Setup Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleMakeAdmin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This should be the email you used to register your VestrioZ account
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Secret Key
                </label>
                <div className="relative">
                  <input
                    type={showSecretKey ? "text" : "password"}
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder="Enter the admin secret key"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <i className={`fas ${showSecretKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Contact the developer for the secret key
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                style={{ backgroundColor: loading ? '#6B7280' : '#222831' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Making Admin...
                  </div>
                ) : (
                  <>
                    <i className="fas fa-crown mr-2"></i>
                    Make Me Admin
                  </>
                )}
              </button>
            </form>

            {/* Instructions */}
            <div className="mt-8 p-4 rounded-xl" style={{ backgroundColor: '#F6F1EB' }}>
              <h3 className="font-semibold mb-2" style={{ color: '#222831' }}>
                Instructions:
              </h3>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Enter the email address you used to register on VestrioZ</li>
                <li>Enter the admin secret key: <code className="bg-gray-200 px-1 rounded">VESTRIOZ_ADMIN_SECRET_2024</code></li>
                <li>Click "Make Me Admin"</li>
                <li>Login with your regular credentials</li>
                <li>Access the admin dashboard at <code className="bg-gray-200 px-1 rounded">/admin</code></li>
              </ol>
            </div>

            {/* Navigation */}
            <div className="mt-6 text-center space-y-2">
              <Link
                to="/login"
                className="block text-blue-600 hover:text-blue-800 transition-colors"
              >
                Already have admin access? Login here
              </Link>
              <Link
                to="/"
                className="block text-gray-500 hover:text-gray-700 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>

          {/* Security Note */}
          <div className="mt-6 text-center">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-center justify-center mb-2">
                <i className="fas fa-exclamation-triangle text-yellow-600 mr-2"></i>
                <span className="font-semibold text-yellow-800">Security Note</span>
              </div>
              <p className="text-sm text-yellow-700">
                This setup page should be removed in production. Only use this for initial admin setup.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSetup;